import { useState } from 'react';
import { Modal, Input, GoldBtn } from './UI';
import { submitFilmRequest } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';

// ── EmailJS config ─────────────────────────────────────────────
// 1. Sign up at https://emailjs.com (free)
// 2. Add a Service (Gmail / Outlook etc.)
// 3. Create TWO templates (see README for exact setup)
// 4. Replace the values below with your real IDs

const EMAILJS_PUBLIC_KEY   = 'YOUR_PUBLIC_KEY';    // Account → API Keys
const EMAILJS_SERVICE_ID   = 'YOUR_SERVICE_ID';    // Email Services tab

// Template 1: sent TO YOU (admin) when someone requests a film
// Set "Reply-To" field in the template to: {{requester_email}}
// Template variables: {{film_name}}, {{language}}, {{release_year}},
//                     {{requester_name}}, {{requester_email}}
const EMAILJS_ADMIN_TEMPLATE = 'YOUR_ADMIN_TEMPLATE_ID';

// Template 2: sent TO THE USER confirming their request was received
// Template variables: {{film_name}}, {{requester_name}}, {{to_email}}
// This is optional — leave as '' to skip user confirmation email
const EMAILJS_USER_TEMPLATE  = 'YOUR_USER_TEMPLATE_ID';

// ── Load EmailJS SDK once ──────────────────────────────────────
let ejsReady = false;
async function loadEmailJS() {
  if (ejsReady) return;
  await new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://cdn.jsdelivr.net/npm/@emailjs/browser@3/dist/email.min.js';
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });
  window.emailjs.init(EMAILJS_PUBLIC_KEY);
  ejsReady = true;
}

async function sendEmail(templateId, params) {
  await loadEmailJS();
  return window.emailjs.send(EMAILJS_SERVICE_ID, templateId, params);
}

const configured = EMAILJS_PUBLIC_KEY !== 'YOUR_PUBLIC_KEY' && EMAILJS_SERVICE_ID !== 'YOUR_SERVICE_ID';

// ── Modal ──────────────────────────────────────────────────────
export default function RequestFilmModal({ open, onClose }) {
  const { user, profile } = useAuth();
  const [form, setForm] = useState({ filmName: '', language: '', releaseYear: '', requesterEmail: '', type: 'movie' });
  const [status, setStatus] = useState('idle'); // idle | sending | done | error
  const [errMsg, setErrMsg] = useState('');

  const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  const requesterEmail = user?.email || form.requesterEmail.trim();
  const requesterName  = profile?.username || 'A Critiqr user';

  const handleSend = async () => {
    if (!form.filmName.trim()) { setErrMsg('Film title is required.'); return; }
    if (!form.language.trim()) { setErrMsg('Language is required.'); return; }
    setStatus('sending'); setErrMsg('');

    try {
      // 1. Always log to Supabase admin panel
      await submitFilmRequest({
        filmName:       form.filmName.trim(),
        language:       form.language.trim(),
        releaseYear:    form.releaseYear.trim(),
        requesterEmail: requesterEmail,
        requestedBy:    user?.id || null,
        type:           form.type,
      });

      // 2. Send email to admin (you) with reply-to set to the user
      if (configured) {
        await sendEmail(EMAILJS_ADMIN_TEMPLATE, {
          film_name:       form.filmName.trim(),
          language:        form.language.trim(),
          release_year:    form.releaseYear.trim() || 'Not specified',
          requester_name:  requesterName,
          requester_email: requesterEmail || 'Not provided',
          reply_to:        requesterEmail || '',   // ← Hit Reply → goes to user
        });

        // 3. Send confirmation back to user (if they gave their email)
        if (requesterEmail && EMAILJS_USER_TEMPLATE !== 'YOUR_USER_TEMPLATE_ID') {
          await sendEmail(EMAILJS_USER_TEMPLATE, {
            film_name:      form.filmName.trim(),
            requester_name: requesterName,
            to_email:       requesterEmail,
          });
        }
      }

      setStatus('done');
    } catch (e) {
      console.error('Request error:', e);
      // Still show success — it was logged to Supabase even if email failed
      setStatus('done');
    }
  };

  const reset = () => {
    setStatus('idle'); setErrMsg('');
    setForm({ filmName: '', language: '', releaseYear: '', requesterEmail: '', type: 'movie' });
  };

  return (
    <Modal open={open} onClose={() => { onClose(); reset(); }} title="🎬 Request a Film" width={480}>

      {status === 'done' ? (
        /* ── Success screen ── */
        <div style={{ textAlign: 'center', padding: '20px 0 12px' }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>📨</div>
          <h3 style={{ fontFamily: "'Playfair Display', serif", color: 'var(--gold)', fontSize: 22, marginBottom: 10 }}>
            Request Received!
          </h3>
          <p style={{ color: 'var(--text-dim)', fontSize: 15, lineHeight: 1.65, marginBottom: 8 }}>
            We'll review <strong style={{ color: 'var(--text)' }}>{form.filmName}</strong> and get back to you.
          </p>
          {requesterEmail && (
            <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 24 }}>
              We may reply to <span style={{ color: 'var(--gold-dim)' }}>{requesterEmail}</span> once reviewed.
            </p>
          )}
          <GoldBtn outline onClick={() => { reset(); onClose(); }}>Close</GoldBtn>
        </div>
      ) : (
        /* ── Form ── */
        <>
          <p style={{ color: 'var(--text-dim)', fontSize: 15, lineHeight: 1.6, marginBottom: 20 }}>
            Can't find a film? Tell us and we'll review your request.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Input
              label="Film Title *"
              value={form.filmName}
              onChange={f('filmName')}
              placeholder="e.g. Moothon"
            />
            {/* Movie or Series dropdown */}
            <div>
              <div style={{ color: 'var(--text-dim)', fontSize: 13, marginBottom: 6 }}>Type</div>
              <div style={{ display: 'flex', gap: 8 }}>
                {[['movie', '🎥 Movie'], ['series', '📺 Series']].map(([val, label]) => (
                  <button key={val} type="button"
                    onClick={() => setForm(p => ({ ...p, type: val }))}
                    style={{
                      flex: 1, padding: '9px', cursor: 'pointer',
                      fontFamily: "'Crimson Text', serif", fontSize: 15,
                      borderRadius: 7, border: `1.5px solid ${form.type === val ? 'var(--gold)' : 'var(--border2)'}`,
                      background: form.type === val ? 'rgba(212,175,55,0.1)' : 'var(--surface2)',
                      color: form.type === val ? 'var(--gold)' : 'var(--text-dim)',
                      fontWeight: form.type === val ? 600 : 400,
                    }}>{label}</button>
                ))}
              </div>
            </div>

            <Input
              label="Language *"
              value={form.language}
              onChange={f('language')}
              placeholder="e.g. Malayalam"
            />
            <Input
              label="Release Year"
              value={form.releaseYear}
              onChange={f('releaseYear')}
              placeholder="e.g. 2019"
            />
            <div>
              <Input
                label={user ? 'Your Email (we may reply here)' : 'Your Email — so we can reply to you'}
                type="email"
                value={user ? (user.email || '') : form.requesterEmail}
                onChange={user ? undefined : f('requesterEmail')}
                placeholder="you@example.com"
              />
              <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 5 }}>
                If you provide your email, we can reply to let you know when it's added — or why it can't be.
              </p>
            </div>

            {errMsg && (
              <div style={{ color: 'var(--red)', fontSize: 14, padding: '8px 12px', background: 'rgba(192,57,43,0.1)', borderRadius: 6 }}>
                {errMsg}
              </div>
            )}

            <GoldBtn onClick={handleSend} disabled={status === 'sending'}>
              {status === 'sending' ? '✉ Sending…' : '✉ Send Request'}
            </GoldBtn>

            <p style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'center', lineHeight: 1.5 }}>
              {configured
                ? 'Your request will be emailed to the admin and logged to the review panel.'
                : 'Your request will be logged to the admin panel for review.'}
            </p>
          </div>
        </>
      )}
    </Modal>
  );
}
